import { darken } from 'polished';
import React from 'react';
import styled, { css } from 'styled-components';
import { LFC } from '../../../Legacy';
import { Icon } from '../../../concepts/resources/Icon';
import { RenderBorder, RenderBounds, RenderBox, RenderInner, usePose } from '../../canvas/visualization/RenderHelpers';
import { Counter } from './Counter';

const Button = styled.div<{ color: string, active?: boolean, pressed?: boolean, play?: boolean }>`
    position: absolute;    
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #FFFFFF;
    border-radius: 2px;
    background: ${p => p.active ? (p.pressed ? darken(.1, p.color) : darken(.2, p.color)) : p.color};
    color: ${p => p.active ? '#FFFFFF' : '#DDDDDD'};
    font-size: 10px;
    font-weight: 450;
`;

const ValueText = styled.div<{ shadow?: boolean }>`
    font-family: 'Rubik';
    font-size: 32px;
    font-weight: 450;
    font-variant-numeric: tabular-nums;

    ${p => p.shadow && css`
        opacity: 0.05;
    `};
`;

export const RenderValue = ({ object }: { object: Counter }) => {
    return <RenderBox inset="0 15px" display="flex" alignItems="center" justifyContent="end">
        <ValueText>{object.value}</ValueText>
    </RenderBox>;
};

export const RenderValueShadow = ({ object }: { object: Counter }) => {
    return <RenderBox inset="0 15px" display="flex" alignItems="center" justifyContent="end">
        <ValueText shadow>{object.value?.toString()?.padStart(6, '0')}</ValueText>
    </RenderBox>;
};

const RenderScreen = ({ object, invoke, pressed }: { object: Counter, invoke: string, pressed: boolean }) => {
    const { screen } = object.layout;

    return <RenderBox {...screen.toCSS()} background="#ffffff" borderRadius="4px">
        <RenderValueShadow object={object} />
        <RenderValue object={object} />
    </RenderBox>;
};

export const CounterVisual: LFC<{ object: Counter }> = ({ object }) => {
    const { dec, reset, inc } = object.layout;

    const pose = usePose(object);
    const invoke = pose.interaction?.type === 'invoke' ? pose.interaction.hint : null;
    const pressed = pose.pressed;

    const borderStyling = {
        width: object.width,
        height: object.height,
    };

    return <>
        <RenderBounds object={object}>
            <RenderBorder pose={pose} style={borderStyling}>
                <RenderInner object={object}>
                    <RenderBox inset="0" background={object.color} borderRadius="12px">
                        <RenderScreen object={object} invoke={invoke} pressed={pressed} />
                        <RenderBox {...dec.toCSS()}>
                            <Button color={object.color} active={invoke === 'dec'} pressed={pressed}>
                                <Icon src="deqo://icons/hero/minus" />
                            </Button>
                        </RenderBox>
                        <RenderBox {...reset.toCSS()}>
                            <Button color={object.color} active={invoke === 'reset'} pressed={pressed}>
                                RESET
                            </Button>
                        </RenderBox>
                        <RenderBox {...inc.toCSS()}>
                            <Button color={object.color} active={invoke === 'inc'} pressed={pressed}>
                                <Icon src="deqo://icons/hero/plus" />
                            </Button>
                        </RenderBox>
                    </RenderBox>
                </RenderInner>
            </RenderBorder>
        </RenderBounds>
    </>;
};
